import { ShieldAlert, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Persistent nudge shown to staff who deferred MFA enrollment (see
 * services/mfaDeferral.ts). Sits bottom-right as a fixed card rather than a
 * top bar — the sidebar is fixed-position and content is offset by its width,
 * so a full-width bar would fight that layout.
 *
 * "Dismiss" only hides it for the current page view; it reappears on the next
 * load until the user actually enrols. The deferral itself expires separately,
 * at which point the blocking gate returns.
 */
export default function MfaReminderBanner({ onSetUp }: { onSetUp: () => void }) {
  const { t } = useTranslation();
  const [hidden, setHidden] = useState(false);

  if (hidden) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[120] w-[min(22rem,calc(100vw-2.5rem))] rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-lg">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
          <ShieldAlert size={17} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">
            {t('mfa.reminderTitle', { defaultValue: 'Finish securing your account' })}
          </p>
          <p className="mt-0.5 text-xs text-amber-800">
            {t('mfa.reminderBody', {
              defaultValue:
                'Your school requires two-step verification for staff accounts. It takes about a minute.',
            })}
          </p>
          <button
            type="button"
            onClick={onSetUp}
            className="mt-2 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
          >
            {t('mfa.reminderCta', { defaultValue: 'Set it up now' })}
          </button>
        </div>
        <button
          type="button"
          onClick={() => setHidden(true)}
          aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
          className="-mr-1 -mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-amber-500 transition-colors hover:bg-amber-100 hover:text-amber-700"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  );
}
